import { ValidationRuleId } from '@/lib/graphql/generated/graphql';
import { effectiveValidationErrorsAtom } from '@/states/effective-validation-errors.state';
import { productsState } from '@/states/products.state';
import { atom } from 'jotai';

export type BlockingValidationError = {
  ruleId: ValidationRuleId;
  count: number;
  itemIds: string[];
};

export const RULES_WITH_DEDICATED_SECTION = new Set<ValidationRuleId>([
  ValidationRuleId.DuplicateVariant,
  ValidationRuleId.PsychologicalPriceApplication,
  ValidationRuleId.RequiredCategory,
  ValidationRuleId.RequiredShippingFees,
]);

/**
 * Critical validation errors (severity 'error') grouped by rule, over selected
 * deal items only. Drives the "blocking errors" section of the AlertSidebar so
 * Choose users can understand and act on what blocks closing the offer.
 *
 * `required_fields` is a single backend rule: it is kept whole, consistent with
 * the table's per-rule filter and its count.
 */
export const blockingValidationErrorsAtom = atom<BlockingValidationError[]>(
  (get) => {
    const products = get(productsState);
    const effectiveErrors = get(effectiveValidationErrorsAtom);

    const grouped = new Map<ValidationRuleId, string[]>();

    for (const product of products) {
      if (!product.activeVersion.isSelected) continue;
      const errors = effectiveErrors.get(product.id);
      if (errors == null) continue;
      for (const error of errors) {
        if (error.severity !== 'error') continue;

        const ids = grouped.get(error.ruleId);
        if (ids) {
          if (!ids.includes(product.id)) ids.push(product.id);
        } else {
          grouped.set(error.ruleId, [product.id]);
        }
      }
    }

    return [...grouped.entries()]
      .map(([ruleId, itemIds]) => ({
        ruleId,
        count: itemIds.length,
        itemIds,
      }))
      .sort((a, b) => a.ruleId.localeCompare(b.ruleId));
  }
);

/**
 * Distinct raw (French) field labels that are missing across selected items
 * flagged with the `required_fields` rule. Parsed from the backend message
 * (`Champs obligatoires manquants: …`), deduplicated and sorted. Powers the
 * "Données manquantes" card description in the AlertSidebar; translate for
 * display with `translateFieldNames`.
 */
export const missingRequiredFieldLabelsAtom = atom<string[]>((get) => {
  const products = get(productsState);
  const effectiveErrors = get(effectiveValidationErrorsAtom);

  const labels = new Set<string>();
  for (const product of products) {
    if (!product.activeVersion.isSelected) continue;
    const errors = effectiveErrors.get(product.id);
    if (errors == null) continue;
    for (const error of errors) {
      if (error.ruleId !== ValidationRuleId.RequiredFields) continue;
      const match = error.message.match(/manquants:\s*(.+)$/);
      if (!match) continue;
      for (const field of match[1].split(',')) {
        const label = field.trim();
        if (label) labels.add(label);
      }
    }
  }

  return [...labels].sort((a, b) => a.localeCompare(b));
});
